import type { FeedConnection, FeedEvent } from './feeds';

// Pure helpers for the bank feed screens, tested without a server (feedLib.test.ts).

type ConnectionLike = Pick<FeedConnection, 'status' | 'sync_requested_at' | 'last_attempt_at' | 'last_synced_at' | 'last_error' | 'reason'>;

/** A sync was asked for after the last attempt (and not so long ago that it was lost; the API gives up after 15 minutes). */
export function isSyncing(c: Pick<ConnectionLike, 'status' | 'sync_requested_at' | 'last_attempt_at'>, now = new Date()): boolean {
  if (c.status !== 'active' || !c.sync_requested_at) return false;
  const requested = Date.parse(c.sync_requested_at);
  if (now.getTime() - requested > 15 * 60_000) return false;
  return !c.last_attempt_at || requested > Date.parse(c.last_attempt_at);
}

/** "just now", "5 min ago", "3 hours ago", "2 days ago". */
export function ago(iso: string, now = new Date()): string {
  const minutes = Math.max(0, Math.round((now.getTime() - Date.parse(iso)) / 60_000));
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
  const days = Math.round(hours / 24);
  return `${days} ${days === 1 ? 'day' : 'days'} ago`;
}

export type FeedTone = 'done' | 'pending' | 'overdue';

/** The one-line state of a connection for a card or the bell. */
export function feedState(c: ConnectionLike, now = new Date()): { tone: FeedTone; text: string } {
  if (c.status === 'disconnected') return { tone: 'pending', text: 'Disconnected' };
  if (c.status === 'needs_attention') return { tone: 'overdue', text: 'Needs attention' };
  if (isSyncing(c, now)) return { tone: 'pending', text: 'Syncing…' };
  if (c.last_error) return { tone: 'pending', text: 'Last sync failed, trying again within the hour' };
  if (c.last_synced_at) return { tone: 'done', text: `Synced ${ago(c.last_synced_at, now)}` };
  return { tone: 'pending', text: 'Waiting for the first sync' };
}

/** The connection and feed of a bank account, if a live feed fills it. */
export function feedOf<C extends Pick<FeedConnection, 'accounts'>>(feeds: C[], accountId: string): { connection: C; account: NonNullable<C['accounts']>[number] } | null {
  for (const connection of feeds) {
    const account = connection.accounts?.find((a) => a.account_id === accountId);
    if (account) return { connection, account };
  }
  return null;
}

/**
 * The accounts a balance can link to: assets (a Wise balance is money in a bank) kept in the
 * balance's currency (ADR 0029), without a feed of their own.
 */
export function accountsForBalance<A extends { id: string; type: string }>(accounts: readonly A[], feeds: Pick<FeedConnection, 'accounts'>[], currency: string, currencyOf: (a: A) => string): A[] {
  return accounts.filter((a) => a.type === 'asset' && currencyOf(a) === currency && !feedOf(feeds, a.id));
}

/** The day after an ISO date. */
export function nextDay(iso: string): string {
  const d = new Date(`${iso.slice(0, 10)}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
}

/**
 * A start date to offer before the dry run says better: the day after the last reconciliation, or
 * the first of the current month. The dry run then offers the day after the account's latest line.
 */
export function suggestedStart(lastReconciled: string | null, today: string): string {
  return lastReconciled ? nextDay(lastReconciled) : `${today.slice(0, 8)}01`;
}

const COMMON_ZONES = ['UTC', 'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles', 'Europe/London', 'Europe/Berlin', 'Africa/Johannesburg'];

/** UTC first (the default), then the browser's zone, then a few common ones, without repeats. */
export function timeZoneChoices(browserZone: string | undefined): string[] {
  return [...new Set(['UTC', ...(browserZone ? [browserZone] : []), ...COMMON_ZONES])];
}

const EVENT_TEXT: Record<FeedEvent['kind'], string> = {
  connected: 'Connected',
  token_replaced: 'Token replaced',
  balance_linked: 'Balance linked',
  balance_unlinked: 'Balance unlinked',
  disconnected: 'Disconnected',
  sync_requested: 'Sync asked for',
  sync_finished: 'Sync finished',
  credential_read: 'Token read',
  credential_resealed: 'Token re-encrypted',
  attention_needed: 'Needs attention',
};

/** One audit trail entry as a sentence: what happened, who or what did it, and the detail that matters. */
export function eventText(e: Pick<FeedEvent, 'kind' | 'actor' | 'user_name' | 'detail'>): string {
  const who = e.actor === 'member' ? (e.user_name ?? 'A member') : e.actor === 'cron' ? 'Hourly sync' : 'The sync';
  const d = e.detail;
  let extra = '';
  if (e.kind === 'sync_finished') {
    if (d.outcome === 'ok') extra = `${Number(d.lines_new ?? 0)} new of ${Number(d.lines_read ?? 0)} read`;
    else if (typeof d.error === 'string') extra = d.error;
    else if (typeof d.note === 'string') extra = d.note;
  } else if (e.kind === 'attention_needed' && typeof d.reason === 'string') extra = d.reason;
  else if (e.kind === 'credential_read' && typeof d.purpose === 'string') extra = `for ${d.purpose === 'link' ? 'linking' : d.purpose}`;
  else if (e.kind === 'balance_linked' && typeof d.currency === 'string') extra = `${d.currency} from ${String(d.start_date ?? '')}`;
  else if (e.kind === 'balance_unlinked' && d.reason === 'account archived') extra = 'its bank account was archived';
  return [`${EVENT_TEXT[e.kind]} · ${who}`, extra].filter(Boolean).join(' · ');
}

/** API errors about the pasted token itself, shown on the token field rather than at the bottom. */
const TOKEN_ERRORS = new Set(['token_not_read_only', 'wise_token_refused', 'wise_profile', 'wise_profile_mismatch', 'wise_approval_required']);
export const isTokenError = (code: string | undefined) => code !== undefined && TOKEN_ERRORS.has(code);
